import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api, initApiClient, clearApiClient, revokeRefreshToken, extractApiError } from '@/api/client'
import type {
  AuthUser,
  LoginResponse,
  LoginOutcome,
  TwoFactorChallenge,
  AnyRole,
  GlobalRole,
  SiteRole,
} from '@/types/jispman'
import { isTwoFactorChallenge } from '@/types/jispman'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<AuthUser | null>(null)
  const accessToken = ref<string | null>(localStorage.getItem('accessToken'))
  const refreshToken = ref<string | null>(localStorage.getItem('refreshToken'))
  const loading = ref(false)
  const error = ref<string | null>(null)

  const isAuthenticated = computed(() => !!user.value && !!accessToken.value)
  const globalRole = computed(() => user.value?.globalRole ?? null)
  const isSuperAdmin = computed(() => user.value?.globalRole === 'super_admin')
  const isSiteOwner = computed(
    () => user.value?.globalRole === 'site_owner' || user.value?.globalRole === 'super_admin',
  )

  function hasGlobalRole(role: GlobalRole): boolean {
    if (user.value?.globalRole === 'super_admin') return true
    return user.value?.globalRole === role
  }

  function hasSiteAccess(siteId: string): boolean {
    if (user.value?.globalRole === 'super_admin') return true
    return user.value?.siteIds?.includes(siteId) ?? false
  }

  function ownsSite(siteId: string): boolean {
    if (user.value?.globalRole === 'super_admin') return true
    return user.value?.ownedSiteIds?.includes(siteId) ?? false
  }

  function _persistTokens(at: string, rt: string) {
    accessToken.value = at
    refreshToken.value = rt
    localStorage.setItem('accessToken', at)
    localStorage.setItem('refreshToken', rt)
    initApiClient({
      accessToken: at,
      refreshToken: rt,
      onLogout: logout,
      onTokens: t => { accessToken.value = t.accessToken; refreshToken.value = t.refreshToken },
    })
  }

  /**
   * The pending second factor, when there is one.
   *
   * Held in the store rather than returned and forgotten, because the login page needs it
   * across a re-render and because nothing else in the app may act while it is set: the user is
   * half-signed-in, which in practice means not signed in at all. It is a handle, not a
   * credential — it cannot read or write anything on its own.
   */
  const pendingTwoFactor = ref<TwoFactorChallenge | null>(null)

  /** Adopt a completed login: tokens, user, then the full profile. */
  async function _adopt(result: LoginResponse): Promise<void> {
    _persistTokens(result.accessToken, result.refreshToken)
    user.value = { ...result.user, siteIds: [], ownedSiteIds: [], siteRoles: {} }
    // Fetch full profile (populates siteIds, ownedSiteIds, siteRoles from /auth/me)
    await fetchMe()
  }

  /**
   * Step one. Resolves to `'done'` when the password was enough, or `'2fa'` when a code is
   * needed next — in which case NO token has been issued and `pendingTwoFactor` holds the
   * handle. The caller branches on the return value instead of inspecting the response.
   */
  async function login(email: string, password: string): Promise<'done' | '2fa'> {
    loading.value = true
    error.value = null
    pendingTwoFactor.value = null
    try {
      const res = await api.post<{ data: LoginOutcome }>('/auth/login', { email, password })
      const outcome = res.data.data

      if (isTwoFactorChallenge(outcome)) {
        pendingTwoFactor.value = outcome
        return '2fa'
      }

      await _adopt(outcome)
      return 'done'
    } catch (err) {
      error.value = extractApiError(err)
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Step two. Exchanges the pending handle plus a code for a real session.
   *
   * Returns `usedRecoveryCode` so the UI can tell somebody that one of their ten codes is now
   * spent — the alternative is discovering it the next time they need one.
   */
  async function completeTwoFactor(code: string): Promise<{ usedRecoveryCode: boolean }> {
    const challenge = pendingTwoFactor.value
    if (!challenge) throw new Error('No sign-in is waiting for a code.')

    loading.value = true
    error.value = null
    try {
      const res = await api.post<{ data: LoginResponse }>('/auth/login/2fa', {
        challengeToken: challenge.challengeToken,
        code: code.trim(),
      })
      await _adopt(res.data.data)
      pendingTwoFactor.value = null
      return { usedRecoveryCode: res.data.data.usedRecoveryCode ?? false }
    } catch (err) {
      error.value = extractApiError(err)
      throw err
    } finally {
      loading.value = false
    }
  }

  /** Abandon a pending second factor and return to the password form. */
  function cancelTwoFactor(): void {
    pendingTwoFactor.value = null
    error.value = null
  }

  async function fetchMe(): Promise<void> {
    if (!accessToken.value) return
    initApiClient({
      accessToken: accessToken.value,
      refreshToken: refreshToken.value ?? '',
      onLogout: logout,
      // Keeps the store in step when the client rotates the pair, so this very call can never
      // hand a spent token back to the client later. See the note in api/client.ts.
      onTokens: t => { accessToken.value = t.accessToken; refreshToken.value = t.refreshToken },
    })
    try {
      const res = await api.get<{ data: AuthUser }>('/auth/me')
      user.value = res.data.data
    } catch {
      user.value = null
    }
  }

  /**
   * Re-entrancy guard.
   *
   * `logout` is also the api client's `onLogout` hook, so a failing refresh calls it — and the
   * old implementation then made a request that could itself 401 and call it again.
   */
  let loggingOut = false

  /**
   * Sign out locally FIRST, then tell the server as a courtesy.
   *
   * The old order was the other way round: `await api.post('/auth/logout')` and only then
   * clear. Two problems, both seen while testing the idle hang on 2026-10-25. The call went
   * through the authenticated instance, so it carried the token that had just been rejected
   * and tripped the 401 interceptor into another refresh — up to fifteen more seconds, and a
   * second `onLogout` on the way out. And until all that settled the tokens were still in
   * localStorage, so the app sat "signed in" with credentials that could not work rather than
   * returning the person to the login page.
   *
   * Signing out is a local act. It cannot be contingent on reaching a server, least of all the
   * server we have just failed to reach.
   */
  async function logout(): Promise<void> {
    if (loggingOut) return
    loggingOut = true

    const spentRefreshToken = refreshToken.value

    // A handle left behind would reappear as a stale code prompt on the next sign-in attempt.
    pendingTwoFactor.value = null
    user.value = null
    accessToken.value = null
    refreshToken.value = null
    localStorage.removeItem('accessToken')
    localStorage.removeItem('refreshToken')
    // Clears the module's copies too, so an in-flight 401 cannot start a refresh behind us.
    clearApiClient()

    try {
      if (spentRefreshToken) await revokeRefreshToken(spentRefreshToken)
    } finally {
      loggingOut = false
    }
  }

  async function init(): Promise<void> {
    if (accessToken.value) {
      await fetchMe()
    }
  }

  return {
    user,
    accessToken,
    loading,
    error,
    isAuthenticated,
    globalRole,
    isSuperAdmin,
    isSiteOwner,
    hasGlobalRole,
    hasSiteAccess,
    ownsSite,
    login,
    pendingTwoFactor,
    completeTwoFactor,
    cancelTwoFactor,
    fetchMe,
    logout,
    init,
  }
})
